import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import AICheckerResults from 'notebook/ai-tools/AICheckerResults';
import {CheckEntryResponseState} from 'notebook/ai-tools/NotebookAIToolsConstants';
import useCheckEntryResultInfo from 'notebook/ai-tools/useCheckEntryResultInfo';
import {FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {smallSpacingTop} from 'styles/Utilities';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Pill, {PillSize, PillStyle} from 'ui-library/buttons/Pill';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AICheckerResultsContainerProps {
  entryId: string;
}

/**
 * Top-level UI for the AI notebook checker results. This component is mounted within the review tab
 * and is responsible for fetching and displaying results, and sending relevant analytics events.
 *
 * See useCheckEntryResultInfo for details on the data fetching lifecycle.
 */
export default function AICheckerResultsContainer({entryId}: AICheckerResultsContainerProps): JSX.Element {
  const {checkEntryResults, onToggleCompleted, onToggleUsefulState, onRecompute} =
    useCheckEntryResultInfo(entryId);
  const [isCollapsed, setCollapsed] = useState(false);
  return (
    <div className={css(styles.resultsContainer)}>
      <div className={css(styles.titleBar)}>
        <div className={css(styles.title)}>
          <h4>Notebook check</h4>
          <Pill size={PillSize.MINI} style={PillStyle.PRIMARY} icon="flask" text="BETA" />
          {!isCollapsed && checkEntryResults.state !== CheckEntryResponseState.LOADING && (
            <span className={css(styles.recomputeIcon)}>
              <Tooltip text="Re-run checks">
                <BenchlingIcon
                  icon="refresh"
                  onClick={() => {
                    trackEvent(ELN_ANALYTICS_ACTIONS.RERAN_AI_NOTEBOOK_CHECKER, {
                      traceId:
                        checkEntryResults.state === CheckEntryResponseState.SUCCESS
                          ? checkEntryResults.traceId
                          : null,
                      entryId,
                    });
                    onRecompute();
                  }}
                />
              </Tooltip>
            </span>
          )}
        </div>
        <div className={css(styles.expandCollapseIcon)}>
          <BenchlingIcon
            icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
            altText={isCollapsed ? 'Expand' : 'Collapse'}
            onClick={() => {
              setCollapsed((collapsed) => !collapsed);
            }}
          />
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className={css(styles.banner)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="This feature is powered by AI, and may make mistakes."
              supportingInfo={
                <span>
                  The AI system will analyze the content of your entry. Attachments and linked items are not
                  yet supported. See the{' '}
                  <a
                    href="https://help.benchling.com/hc/en-us/articles/27659453707405"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    help article
                  </a>{' '}
                  for more details. For more information on AI security and privacy, look{' '}
                  <a
                    href="https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </span>
              }
              action={{actionType: InlineBannerActionType.ACCORDION}}
            />
          </div>
          <div className={css(styles.results)}>
            <AICheckerResults
              checkEntryResults={checkEntryResults}
              onToggleCompleted={onToggleCompleted}
              onToggleUsefulState={onToggleUsefulState}
              onRecompute={onRecompute}
            />
          </div>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  resultsContainer: {
    padding: 16,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: Color.GRAY_300,
    marginBottom: 12,
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  recomputeIcon: {
    cursor: 'pointer',
    ...FONT_PROXIMA_16,
  },
  expandCollapseIcon: {
    cursor: 'pointer',
    ...FONT_PROXIMA_16,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    gap: 8,
  },
  banner: {
    ...smallSpacingTop,
  },
  results: {
    ...smallSpacingTop,
  },
});
